import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { contractService } from '../services/contractService';
import toast from 'react-hot-toast';

interface WithdrawalLimits {
  minimumWithdrawal: number;
  maximumWithdrawal: number;
  maxWithdrawalPerDay: number;
  maxWithdrawalPerWeek: number;
  maxWithdrawalPerMonth: number;
}

interface PurchaseLimits {
  maxMultiplierPurchase: number;
  maxAutoClickerPurchase: number;
}

const DEFAULT_LIMITS: WithdrawalLimits = {
  minimumWithdrawal: 1000,
  maximumWithdrawal: 100000,
  maxWithdrawalPerDay: 50000,
  maxWithdrawalPerWeek: 250000,
  maxWithdrawalPerMonth: 1000000
};

const DEFAULT_PURCHASE_LIMITS: PurchaseLimits = {
  maxMultiplierPurchase: 10,
  maxAutoClickerPurchase: 5
};

export function useContract() {
  const [contract, setContract] = useState<ethers.Contract | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [limits, setLimits] = useState<WithdrawalLimits>(DEFAULT_LIMITS);
  const [purchaseLimits, setPurchaseLimits] = useState<PurchaseLimits>(DEFAULT_PURCHASE_LIMITS);

  useEffect(() => {
    const initContract = async () => {
      try {
        setLoading(true);
        setError(null);

        await contractService.initialize();
        contractService.setupEventListeners();

        setContract(contractService.contract);

        if (!isDemoMode && contractService.contract) {
          const contractLimits = await Promise.all([
            contractService.contract.minimumWithdrawal(),
            contractService.contract.maximumWithdrawal(),
            contractService.contract.maxWithdrawalPerDay(),
            contractService.contract.maxWithdrawalPerWeek(),
            contractService.contract.maxWithdrawalPerMonth(),
            contractService.contract.maxMultiplierPurchase(),
            contractService.contract.maxAutoClickerPurchase()
          ]);

          setLimits({
            minimumWithdrawal: Number(contractLimits[0]),
            maximumWithdrawal: Number(contractLimits[1]),
            maxWithdrawalPerDay: Number(contractLimits[2]),
            maxWithdrawalPerWeek: Number(contractLimits[3]),
            maxWithdrawalPerMonth: Number(contractLimits[4])
          });

          setPurchaseLimits({
            maxMultiplierPurchase: Number(contractLimits[5]),
            maxAutoClickerPurchase: Number(contractLimits[6])
          });
        }
      } catch (err) {
        console.error('Contract initialization error:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize contract'));
        toast.error('Failed to connect to game contract');
      } finally {
        setLoading(false);
      }
    };

    initContract();

    return () => {
      contractService.removeEventListeners();
    };
  }, [isDemoMode]);

  const registerClicks = async (clicks: number) => {
    try {
      setLoading(true);
      await contractService.registerClicks(clicks);
      toast.success(`Successfully registered ${clicks} clicks`);
    } catch (err) {
      console.error('Error registering clicks:', err);
      toast.error('Failed to register clicks');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async (coinsAmount: number) => {
    try {
      setLoading(true);
      await contractService.withdraw(coinsAmount);
      toast.success('Withdrawal successful');
    } catch (err) {
      console.error('Error withdrawing:', err);
      toast.error('Failed to process withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateConversionRate = async (rate: number) => {
    if (isDemoMode) {
      toast.success('Conversion rate updated (Demo Mode)');
      return;
    }

    try {
      setLoading(true);
      await contractService.contract?.updateConversionRate(rate);
      toast.success('Conversion rate updated successfully');
    } catch (err) {
      console.error('Error updating conversion rate:', err);
      toast.error('Failed to update conversion rate');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateWithdrawalLimits = async (
    minimumWithdrawal: number,
    maximumWithdrawal: number,
    maxWithdrawalPerDay: number,
    maxWithdrawalPerWeek: number,
    maxWithdrawalPerMonth: number
  ) => {
    if (isDemoMode) {
      setLimits({
        minimumWithdrawal,
        maximumWithdrawal,
        maxWithdrawalPerDay,
        maxWithdrawalPerWeek,
        maxWithdrawalPerMonth
      });
      toast.success('Withdrawal limits updated (Demo Mode)');
      return;
    }

    try {
      setLoading(true);
      await contractService.contract?.updateWithdrawalLimits(
        minimumWithdrawal,
        maximumWithdrawal,
        maxWithdrawalPerDay,
        maxWithdrawalPerWeek,
        maxWithdrawalPerMonth
      );

      setLimits({
        minimumWithdrawal,
        maximumWithdrawal,
        maxWithdrawalPerDay,
        maxWithdrawalPerWeek,
        maxWithdrawalPerMonth
      });

      toast.success('Withdrawal limits updated successfully');
    } catch (err) {
      console.error('Error updating withdrawal limits:', err);
      toast.error('Failed to update withdrawal limits');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePurchaseLimits = async (
    maxMultiplierPurchase: number,
    maxAutoClickerPurchase: number
  ) => {
    if (isDemoMode) {
      setPurchaseLimits({
        maxMultiplierPurchase,
        maxAutoClickerPurchase
      });
      toast.success('Purchase limits updated (Demo Mode)');
      return;
    }

    try {
      setLoading(true);
      await contractService.contract?.updatePurchaseLimits(
        maxMultiplierPurchase,
        maxAutoClickerPurchase
      );

      setPurchaseLimits({
        maxMultiplierPurchase,
        maxAutoClickerPurchase
      });

      toast.success('Purchase limits updated successfully');
    } catch (err) {
      console.error('Error updating purchase limits:', err);
      toast.error('Failed to update purchase limits');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const toggleDemoMode = () => {
    setIsDemoMode(!isDemoMode);
    if (!isDemoMode) {
      setLimits(DEFAULT_LIMITS);
      setPurchaseLimits(DEFAULT_PURCHASE_LIMITS);
    }
    toast.success(`Demo mode ${!isDemoMode ? 'enabled' : 'disabled'}`);
  };

  return {
    contract,
    loading,
    error,
    isDemoMode,
    limits,
    purchaseLimits,
    registerClicks,
    withdraw,
    updateConversionRate,
    updateWithdrawalLimits,
    updatePurchaseLimits,
    toggleDemoMode,
    contractService
  };
}